import { useCallback, useRef, useState } from 'react';
import type { EventInput } from '@fullcalendar/core';

/**
 * Hold the events array steady while a drag is in flight.
 *
 * FullCalendar abandons an active gesture when its `events` prop is replaced,
 * `eventDrop` never runs and the block snaps back to its old slot. Uses refs
 * during drag so `beginDrag` does not re-render (a setState mid-gesture breaks
 * touch drags on mobile).
 */
export function useStableCalendarEvents(events: EventInput[]) {
  const isDraggingRef = useRef(false);
  const frozenRef = useRef(events);
  const [tick, setTick] = useState(0);

  if (!isDraggingRef.current) {
    frozenRef.current = events;
  }

  const beginDrag = useCallback(() => {
    frozenRef.current = events;
    isDraggingRef.current = true;
  }, [events]);

  const endDrag = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setTick((n) => n + 1);
  }, []);

  void tick;

  return {
    displayEvents: isDraggingRef.current ? frozenRef.current : events,
    beginDrag,
    endDrag,
  };
}
